import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ShieldOff, LogOut, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getUsage } from "@/lib/api";
import ContactModal from "@/components/ContactModal";

/**
 * Full-screen block shown to authenticated users who are NOT in the whitelist.
 * They can send an in-app contact form (which also registers the request in
 * the /admin panel) or sign out.
 *
 * Polls /api/usage every 15s so that when the admin approves them from the
 * dashboard, they are automatically taken to the app without needing to
 * refresh manually (bug C fix · Feb 2026 v36).
 */
export default function AccessBlockedScreen() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [checking, setChecking] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const pollRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    const poll = async () => {
      try {
        const usage = await getUsage();
        // ONLY trust `whitelisted === true` · a non-whitelisted user gets
        // `reached=false` too (because count=0 < default_limit=10), which
        // previously produced a false "approved" toast on every poll.
        if (usage?.whitelisted === true && !notifiedRef.current) {
          notifiedRef.current = true;
          if (pollRef.current) clearInterval(pollRef.current);
          toast.success(t("blocked.access_granted_toast"), {
            duration: 4000,
            description: t("blocked.access_granted_desc"),
          });
          // Small delay so the user sees the toast, then reload the app.
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        }
      } catch {
        /* ignore · will retry on next tick */
      }
    };
    // Poll every 15s. Cheap: `/api/usage` is a tiny endpoint.
    pollRef.current = setInterval(poll, 15000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [t]);

  const handleManualCheck = async () => {
    setChecking(true);
    try {
      const usage = await getUsage();
      if (usage?.whitelisted === true) {
        toast.success(t("blocked.access_granted_toast"));
        setTimeout(() => window.location.reload(), 800);
      } else {
        toast(t("blocked.still_pending"));
      }
    } catch {
      toast.error(t("blocked.check_error"));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      data-testid="access-blocked-screen"
    >
      <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-8 md:p-10 text-center shadow-2xl">
        <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-6">
          <ShieldOff size={28} className="text-red-400" />
        </div>
        <h1 className="text-2xl md:text-3xl font-display font-bold text-white mb-3">
          {t("blocked.title")}
        </h1>
        <p className="text-sm text-[color:var(--fg-secondary)] mb-6 leading-relaxed">
          {t("blocked.body")}
        </p>
        {user?.email && (
          <div className="text-xs text-[color:var(--fg-muted)] mb-6">
            {user.email}
          </div>
        )}
        <div className="flex flex-col gap-3">
          {requestSent ? (
            <div
              className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-4 flex items-start gap-3 text-left"
              data-testid="blocked-request-sent-card"
            >
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-emerald-200 mb-1">
                  {t("blocked.request_sent_title")}
                </div>
                <div className="text-xs text-emerald-100/80 leading-relaxed">
                  {t("blocked.request_sent_body")}
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-sm border border-emerald-500/30 transition-colors"
              data-testid="blocked-contact-admin"
            >
              <MessageCircle size={14} />
              {t("blocked.contact_admin")}
            </button>
          )}
          <button
            onClick={handleManualCheck}
            disabled={checking}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs text-[color:var(--fg-secondary)] hover:text-white border border-white/10 hover:border-white/20 transition-colors disabled:opacity-50"
            data-testid="blocked-manual-check-btn"
          >
            {checking ? <Loader2 className="animate-spin" size={12} /> : null}
            {t("blocked.check_status")}
          </button>
          <button
            onClick={logout}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm text-[color:var(--fg-secondary)] hover:text-white transition-colors"
            data-testid="blocked-logout-btn"
          >
            <LogOut size={14} />
            {t("blocked.logout")}
          </button>
        </div>
        <div className="mt-6 text-[10px] text-[color:var(--fg-muted)] opacity-60">
          {t("blocked.auto_refresh_hint")}
        </div>
      </div>

      <ContactModal
        open={showModal}
        onClose={() => setShowModal(false)}
        category="access_request"
        defaultSubject={t("blocked.contact_default_subject")}
        kicker={t("blocked.contact_kicker")}
        onSent={() => {
          setRequestSent(true);
          // Show a dedicated toast that supersedes the generic "sent" toast
          // fired by ContactModal, so the user knows what happens next.
          toast.success(t("blocked.request_sent_toast_title"), {
            description: t("blocked.request_sent_toast_desc"),
            duration: 5000,
          });
        }}
      />
    </div>
  );
}
